<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <div class="accordion-block" :class="themeColor">
      <Accordion type="freePanel">
        <template #title>
          <heading
            :tagField="headingSizeField"
            class="title"
            :textField="headerField"
          />
        </template>
        <template #body>
          <AccordionItem
            v-for="item in accordionItems"
            :key="item.id"
            :is-open="isEditMode"
            :is-disabled="isEditMode"
            :inverted="isDarkTheme"
          >
            <template #header v-if="getBlockTitle(item.value)">
              <heading
                :tagField="titleHeadingSizeField"
                class="question-title"
                :textField="getBlockTitle(item.value)"
              />
            </template>
            <template #content v-if="getBlockDescription(item.value)">
              <div class="accordion-content">
                <sc-rich-text tag="div" :field="getBlockDescription(item.value)" :editable="false"/>
              </div>
            </template>
          </AccordionItem>
        </template>
        <template #link>
          <sc-link v-if="isEditMode" class="sc-link" :field="linkField"/>
          <sh-button
            v-if="linkHref && !isEditMode"
            class="accordion-link"
            :naked="true"
            :color="buttonColor"
            :href="linkHref"
            :target="linkTarget"
            :aria-label="linkHiddenText"
          >
            {{ linkText }}
          </sh-button>
        </template>
      </Accordion>
    </div>
  </sc-render>
</template>

<script>
import { Accordion, AccordionItem, ShIcon } from '@sohohouse/design-system'
import { Text, Link, RichText } from '@sitecore-jss/sitecore-jss-vue'
import { createNamespacedHelpers } from 'vuex'

import CTA from '~/constants/appearance'
import ThemeMixin from '~/mixins/theme'
import mapFields from '~/helpers/map-fields'
import get from '~/helpers/get'
import { arrayUID } from '~/helpers/unique-id'
import { decreaseTagValue } from '~/helpers/decrease-tag-value'
import Heading from '~/components/sitecore/heading'
import ScRender from '~/components/sitecore/sc-render'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'AccordionBlock',

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  mixins: [ThemeMixin],

  data () {
    return {
      // even fields id can be repeated
      accordionItems: arrayUID(get(this.fields, 'listOfItems'))
    }
  },

  components: {
    Accordion,
    AccordionItem,
    ShIcon,
    ScRender,
    Heading,
    ScText: Text,
    ScLink: Link,
    ScRichText: RichText
  },

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapFields([
      'header',
      'headingSize',
      'link.value'
    ]),

    titleHeadingSizeField () {
      return decreaseTagValue(this.headingSizeField)
    },
    themeColor () {
      return `theme-${this.theme.color}`.toLowerCase()
    },
    linkHref () {
      return get(this.linkField, 'href', '')
    },
    linkTarget () {
      return get(this.linkField, 'target', '')
    },
    linkText () {
      return get(this.linkField, 'text', '')
    },
    linkHiddenText () {
      return get(this.linkField, 'data-hidden-text')
    },
    buttonColor () {
      return this.isDarkTheme ? CTA.SECONDARY : CTA.PRIMARY
    }
  },
  methods: {
    getBlockTitle (item) {
      return get(item.fields, 'title', '')
    },
    getBlockDescription (item) {
      return get(item.fields, 'description', '')
    }
  }
}
</script>

<style lang="scss" scoped>
.accordion-block {
  margin: $space-080 $space-050 $space-070;
  @include tablet {
    margin: $space-100 0;
  }
  .title {
    @include font('080');
    @include tablet {
      @include font('100');
    }
  }
  .question-title {
    @include font('050');
    @include tablet {
      @include font('060');
    }
  }
}

.accordion-content {
  @include font('050');
  padding: $space-060 0 $space-080 0;
  @include tablet {
    padding: $space-070 0 $space-080 0;
    max-width: 600px;
  }
}

</style>
